import {createSlice, createAsyncThunk} from "@reduxjs/toolkit";
import _ from 'lodash';
import config from '../../config'
import * as Api from "../../common/Api";
import {isGuid} from "../../common/utils/Utils";

const initialState = {
    searchCriteria: undefined,
    selectedId: [],
    selectedEvents: [],
    currentPage: 1,
    pageSize: 20,
    order: {by: 'created', direction: 'DESC'},
    loading: false,
    totalRecords: 0,
    reload: false,
    alertMessage: undefined,
    successMessage: undefined,
    managedEvents: [],
    managedListEvent: null,
};

export const getManagedEvents = createAsyncThunk('managedEvents/get-all', async (arg, {getState, requestId}) => {
    const GET_URL = `${config.services.ATLAS.apiUrl}/audit-trail`;
    const managedEvents = await Api.get(true, GET_URL, buildQuery(getState().managedEvents.currentPage, getState().managedEvents.pageSize,
        getState().managedEvents.order.by, getState().managedEvents.order.direction, getState().managedEvents.searchCriteria));
    return managedEvents;
});

export const getManagedEventsWithFilter = createAsyncThunk('managedEvents/get-all-with-filter', async (filter) => {
    const GET_URL = `${config.services.ATLAS.apiUrl}/audit-trail`;
    const managedEvents = await Api.get(true, GET_URL, filter);
    return managedEvents;
});

export const getManagedEventsCount = createAsyncThunk('managedEvents/getCount', async (arg, {getState, requestId}) => {
    try {
        const searchCriteria = getState().managedEvents.searchCriteria ? getState().managedEvents.searchCriteria : '';
        const where = isGuid(searchCriteria) ? {
            where: {
                or: [
                    {
                        id: {
                            eq: searchCriteria
                        }
                    }
                ]
            }
        } :  {
            where: {
                or: [
                    {
                        typeId: {
                            ilike: `%${searchCriteria}%`
                        }
                    },
                ]
            }
        };
        const COUNT_URL = `${config.services.ATLAS.apiUrl}/audit-trail/count`;
        const managedEventsCount = await Api.get(true, COUNT_URL, where ? {filter: where} : undefined);
        return managedEventsCount?.count;
    } catch (err) {
        console.error(err);
    }
});

const buildQuery = (currentPage, pageSize, orderBy, orderDirection, searchCriteria) => {
    if (searchCriteria) {
        const query = isGuid(searchCriteria) ? {
            filter: {
                where: {
                    id: {
                        eq: searchCriteria
                    }
                },
                order: [`${orderBy} ${orderDirection}`],
                limit: pageSize,
                skip: (currentPage - 1) * pageSize
            },
        } : {
            filter: {
                where: {
                    typeId: {
                        ilike: `%${searchCriteria}%`
                    }
                },
                order: [`${orderBy} ${orderDirection}`],
                limit: pageSize,
                skip: (currentPage - 1) * pageSize
            },
        };
        return query;
    } else {
        return  {
            filter: {
                order: [`${orderBy} ${orderDirection}`],
                limit: pageSize,
                skip: (currentPage - 1) * pageSize
            },
        };
    }
};

const managedEventsSlice = createSlice({
    name: 'managedEventsSlice', initialState,
    reducers: {
        clearSelected: {
            reducer(state, action) {
                state.selectedId = [];
                state.selectedMatchTypes = [];
            }
        },
        setCurrentPage: {
            reducer(state, action) {
                state.currentPage = action.payload;
            }
        },
        setOrder: {
            reducer(state, action) {
                state.order.by = _.get(action.payload, 'by');
                state.order.direction = _.get(action.payload, 'direction');
            }
        },
        setSelectedId: {
            reducer(state, action) {
                if (action?.payload?.id) {
                    if (state.selectedId.indexOf(action.payload.id) >= 0) {
                        state.selectedId.splice(state.selectedId.indexOf(action.payload.id), 1);
                        state.selectedMatchTypes.splice(state.selectedMatchTypes.indexOf((list) => list.id === action.payload.id), 1);
                    }
                    if (action.payload.isChecked === true) {
                        state.selectedId.push(action.payload.id);
                        state.selectedMatchTypes.push(action.payload);
                    }
                }
            }
        },
        setSearchCriteria: {
            reducer(state, action) {
                state.searchCriteria = action.payload;
                state.currentPage = 1;
            }
        },
        resetCreateManagedEventsState: {
            reducer(state, action) {
                state.loading = false;
                state.reset = true;
                state.alertMessage = undefined;
                state.successMessage = undefined;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getManagedEvents.pending, (state, action) => {
                state.loading = true;
            })
            .addCase(getManagedEvents.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload) {
                    state.managedEvents = action.payload;
                    state.download = undefined;
                }

                state.alertMessage = undefined;
                state.successMessage = undefined;
            })
            .addCase(getManagedEvents.rejected, (state, action) => {
                state.loading = false;
                state.alertMessage = undefined;
                state.successMessage = undefined;
            })
            .addCase(getManagedEventsCount.pending, (state, action) => {
                state.loading = true;
                state.alertMessage = undefined;
                state.successMessage = undefined;
            })
            .addCase(getManagedEventsCount.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload !== undefined) {
                    state.totalRecords = action.payload;
                }

                state.alertMessage = undefined;
                state.successMessage = undefined;
            })
            .addCase(getManagedEventsCount.rejected, (state, action) => {
                state.loading = false;

                state.alertMessage = undefined;
                state.successMessage = undefined;
            })
            .addCase(getManagedEventsWithFilter.pending, (state, action) => {
                state.loading = true;

                state.alertMessage = undefined;
                state.successMessage = undefined;
            })
            .addCase(getManagedEventsWithFilter.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload) {
                    state.managedEvents = action.payload;
                    state.download = undefined;
                }
                state.alertMessage = undefined;
                state.successMessage = undefined;
            })
            .addCase(getManagedEventsWithFilter.rejected, (state, action) => {
                state.loading = false;
                state.alertMessage = undefined;
                state.successMessage = undefined;
            });
    }
});


export const {
    setCurrentPage,
    setOrder,
    setSelectedId,
    clearSelected,
    setSearchCriteria
} = managedEventsSlice.actions;
export default managedEventsSlice.reducer;
